import React, { useState, useRef, useCallback, useEffect } from 'react';
import { gsap } from 'gsap';
import styles from './Navbar.module.css';

// Background image for the overlay
const overlayBgImage = require('../../images/afriat-yt 1.jpg');

// WhatsApp link with pre-filled message
const WHATSAPP_URL =
  'https://wa.me/972548145494?text=%D7%94%D7%99%D7%99%20%D7%97%D7%9F%2C%20%D7%94%D7%92%D7%A2%D7%AA%D7%99%20%D7%9E%D7%94%D7%90%D7%AA%D7%A8%20%D7%A9%D7%9C%D7%9A%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A9%D7%9E%D7%95%D7%A2%20%D7%A2%D7%95%D7%93%20%D7%A2%D7%9C..';

const INSTAGRAM_URL = 'https://www.instagram.com/afriat_arc/';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showHamburger, setShowHamburger] = useState(false);

  // Refs for DOM elements
  const overlayRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const line3Ref = useRef(null);
  const navLinksRef = useRef([]);
  const separatorRef = useRef(null);
  const contactRef = useRef(null);
  const timelineRef = useRef(null);

  // Clean up GSAP timeline on unmount
  useEffect(() => {
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, []);

  // Show hamburger only after scrolling past the hero section
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      setShowHamburger(scrollY > window.innerHeight * 0.8);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open, compensate for scrollbar width
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  const openMenu = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIsOpen(true);

    // Kill any existing timeline
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    const tl = gsap.timeline({
      onComplete: () => setIsAnimating(false),
    });
    timelineRef.current = tl;

    // Animate hamburger lines to X
    tl.to(line1Ref.current, {
      rotation: 45,
      y: 7.5,
      duration: 0.4,
      ease: 'power2.inOut',
    }, 0)
    .to(line2Ref.current, {
      opacity: 0,
      scaleX: 0,
      duration: 0.2,
      ease: 'power2.in',
    }, 0)
    .to(line3Ref.current, {
      rotation: -45,
      y: -7.5,
      duration: 0.4,
      ease: 'power2.inOut',
    }, 0);

    // Reveal overlay with clip-path circle expanding from top-left
    tl.to(overlayRef.current, {
      clipPath: 'circle(150% at calc(100% - 48px) 48px)',
      duration: 0.8,
      ease: 'power3.inOut',
    }, 0.1);

    // Stagger in nav links
    tl.to(navLinksRef.current, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      stagger: 0.1,
      ease: 'power2.out',
    }, 0.45);

    // Fade in separator
    tl.to(separatorRef.current, {
      opacity: 1,
      duration: 0.4,
      ease: 'power2.out',
    }, 0.75);

    // Fade in contact section
    tl.to(contactRef.current, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: 'power2.out',
    }, 0.8);
  }, [isAnimating]);

  const closeMenu = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);

    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    const tl = gsap.timeline({
      onComplete: () => {
        setIsOpen(false);
        setIsAnimating(false);

        // Reset link styles for next open
        navLinksRef.current.forEach((el) => {
          if (el) {
            gsap.set(el, { opacity: 0, y: 30 });
          }
        });
        if (separatorRef.current) {
          gsap.set(separatorRef.current, { opacity: 0 });
        }
        if (contactRef.current) {
          gsap.set(contactRef.current, { opacity: 0, y: 20 });
        }
      },
    });
    timelineRef.current = tl;

    // Fade out content first
    tl.to(contactRef.current, {
      opacity: 0,
      y: 10,
      duration: 0.25,
      ease: 'power2.in',
    }, 0);

    tl.to(separatorRef.current, {
      opacity: 0,
      duration: 0.2,
      ease: 'power2.in',
    }, 0);

    tl.to(navLinksRef.current, {
      opacity: 0,
      y: -15,
      duration: 0.3,
      stagger: 0.04,
      ease: 'power2.in',
    }, 0);

    // Collapse overlay
    tl.to(overlayRef.current, {
      clipPath: 'circle(0% at calc(100% - 48px) 48px)',
      duration: 0.6,
      ease: 'power3.inOut',
    }, 0.2);

    // Animate X back to hamburger lines
    tl.to(line1Ref.current, {
      rotation: 0,
      y: 0,
      duration: 0.4,
      ease: 'power2.inOut',
    }, 0.3)
    .to(line2Ref.current, {
      opacity: 1,
      scaleX: 1,
      duration: 0.3,
      ease: 'power2.out',
    }, 0.4)
    .to(line3Ref.current, {
      rotation: 0,
      y: 0,
      duration: 0.4,
      ease: 'power2.inOut',
    }, 0.3);
  }, [isAnimating]);

  const toggleMenu = useCallback(() => {
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [isOpen, openMenu, closeMenu]);

  // Handle nav link click - close menu, then scroll
  const handleNavClick = useCallback((sectionId) => {
    if (isAnimating) return;

    // Start closing the menu
    setIsAnimating(true);

    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    const tl = gsap.timeline({
      onComplete: () => {
        setIsOpen(false);
        setIsAnimating(false);

        // Reset link styles
        navLinksRef.current.forEach((el) => {
          if (el) {
            gsap.set(el, { opacity: 0, y: 30 });
          }
        });
        if (separatorRef.current) {
          gsap.set(separatorRef.current, { opacity: 0 });
        }
        if (contactRef.current) {
          gsap.set(contactRef.current, { opacity: 0, y: 20 });
        }

        // Scroll to section after menu closes
        const target = document.getElementById(sectionId);
        if (target) {
          setTimeout(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        }
      },
    });
    timelineRef.current = tl;

    // Fast close animation
    tl.to([...navLinksRef.current, separatorRef.current, contactRef.current], {
      opacity: 0,
      duration: 0.2,
      ease: 'power2.in',
    }, 0);

    tl.to(overlayRef.current, {
      clipPath: 'circle(0% at calc(100% - 48px) 48px)',
      duration: 0.5,
      ease: 'power3.inOut',
    }, 0.1);

    tl.to(line1Ref.current, {
      rotation: 0,
      y: 0,
      duration: 0.35,
      ease: 'power2.inOut',
    }, 0.15)
    .to(line2Ref.current, {
      opacity: 1,
      scaleX: 1,
      duration: 0.25,
      ease: 'power2.out',
    }, 0.25)
    .to(line3Ref.current, {
      rotation: 0,
      y: 0,
      duration: 0.35,
      ease: 'power2.inOut',
    }, 0.15);
  }, [isAnimating]);

  // Store ref callback for nav links array
  const setNavLinkRef = useCallback((index) => (el) => {
    navLinksRef.current[index] = el;
  }, []);

  // Instagram inline SVG icon
  const InstagramIcon = () => (
    <span className={styles.instagramIcon}>
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <circle cx="12" cy="12" r="5" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    </span>
  );

  return (
    <>
      {/* Hamburger / Close Button */}
      <button
        className={`${styles.hamburgerBtn} ${showHamburger || isOpen ? styles.hamburgerVisible : ''}`}
        onClick={toggleMenu}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
      >
        <span className={styles.hamburgerLine} ref={line1Ref} />
        <span className={styles.hamburgerLine} ref={line2Ref} />
        <span className={styles.hamburgerLine} ref={line3Ref} />
      </button>

      {/* Full-screen overlay */}
      <div
        ref={overlayRef}
        className={`${styles.overlay} ${isOpen ? styles.overlayVisible : ''}`}
        aria-hidden={!isOpen}
      >
        {/* Background layers */}
        <div className={styles.overlayBg} />
        <div className={styles.overlayImage}>
          <img src={overlayBgImage} alt="" aria-hidden="true" />
        </div>

        {/* Content */}
        <div className={styles.overlayContent}>
          {/* Navigation links */}
          <nav className={styles.navLinks}>
            <button
              ref={setNavLinkRef(0)}
              className={styles.navLink}
              onClick={() => handleNavClick('projects')}
              type="button"
            >
              פרוייקטים
            </button>
            <button
              ref={setNavLinkRef(1)}
              className={styles.navLink}
              onClick={() => handleNavClick('about')}
              type="button"
            >
              אודות
            </button>
            <button
              ref={setNavLinkRef(2)}
              className={styles.navLink}
              onClick={() => handleNavClick('work-process')}
              type="button"
            >
              תהליך העבודה
            </button>
            <button
              ref={setNavLinkRef(3)}
              className={styles.navLink}
              onClick={() => handleNavClick('faq')}
              type="button"
            >
              שאלות תשובות
            </button>
            <a
              ref={setNavLinkRef(4)}
              className={styles.navLink}
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              צור קשר
            </a>
          </nav>

          {/* Separator */}
          <div ref={separatorRef} className={styles.separator} />

          {/* Contact details */}
          <div ref={contactRef} className={styles.contactSection}>
            <a
              href="tel:054-814-5494"
              className={`${styles.contactItem} ${styles.phoneItem}`}
            >
              054-814-5494
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.contactItem}
            >
              <InstagramIcon />
              @afriat_arc
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
